/**
 * DraggableFab — Draggable Floating Action Button with Edge-Snapping
 *
 * A premium floating button that users can drag to reposition.
 * On release it snaps to the nearest horizontal edge (left or right).
 * Position persists in localStorage. On mobile, drag is disabled.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Fab, Tooltip, Badge, useMediaQuery, useTheme } from '@mui/material';
import { SupportAgent as SupportAgentIcon } from '@mui/icons-material';
import { brand } from '../../theme';

const STORAGE_KEY = 'support-hub-fab-position';
const FAB_SIZE = 56;
const EDGE_MARGIN = 16;
const SNAP_TRANSITION_MS = 300;

interface FabPosition {
    x: number;
    y: number;
    side: 'left' | 'right';
}

interface DraggableFabProps {
    onClick: () => void;
    isPanelOpen: boolean;
    badgeCount?: number;
}

function getDefaultPosition(): FabPosition {
    return {
        x: EDGE_MARGIN,
        y: typeof window !== 'undefined' ? window.innerHeight - FAB_SIZE - EDGE_MARGIN : 600,
        side: 'left',
    };
}

function loadPosition(): FabPosition {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            const parsed = JSON.parse(stored) as FabPosition;
            // Validate bounds
            if (
                typeof parsed.x === 'number' &&
                typeof parsed.y === 'number' &&
                (parsed.side === 'left' || parsed.side === 'right')
            ) {
                return parsed;
            }
        }
    } catch {
        // Ignore parse errors
    }
    return getDefaultPosition();
}

function savePosition(pos: FabPosition) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
    } catch {
        // Ignore quota errors
    }
}

function clampY(y: number): number {
    const max = window.innerHeight - FAB_SIZE - EDGE_MARGIN;
    const min = EDGE_MARGIN;
    return Math.min(Math.max(y, min), max);
}

export const DraggableFab: React.FC<DraggableFabProps> = ({
    onClick,
    isPanelOpen,
    badgeCount = 0,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [position, setPosition] = useState<FabPosition>(loadPosition);
    const [isDragging, setIsDragging] = useState(false);
    const [isSnapping, setIsSnapping] = useState(false);
    const hasDraggedRef = useRef(false);
    const dragStartRef = useRef<{ x: number; y: number; fabX: number; fabY: number } | null>(null);

    // Ensure position stays valid on resize
    useEffect(() => {
        const handleResize = () => {
            setPosition((prev) => {
                const newY = clampY(prev.y);
                const newX =
                    prev.side === 'right'
                        ? window.innerWidth - FAB_SIZE - EDGE_MARGIN
                        : EDGE_MARGIN;
                const updated = { x: newX, y: newY, side: prev.side };
                savePosition(updated);
                return updated;
            });
        };
        window.addEventListener('resize', handleResize);
        // Normalise on mount too (e.g. screen rotated since last visit)
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // ----- Pointer event handlers -----
    const handlePointerDown = useCallback(
        (e: React.PointerEvent) => {
            e.preventDefault();
            // Capture pointer to get moves even outside window
            (e.target as HTMLElement).setPointerCapture(e.pointerId);
            hasDraggedRef.current = false;
            dragStartRef.current = {
                x: e.clientX,
                y: e.clientY,
                fabX: position.x,
                fabY: position.y,
            };
            setIsDragging(true);
        },
        [position.x, position.y],
    );

    const handlePointerMove = useCallback(
        (e: React.PointerEvent) => {
            if (!dragStartRef.current) return;
            const dx = e.clientX - dragStartRef.current.x;
            const dy = e.clientY - dragStartRef.current.y;

            // Only count as a drag if moved more than 5px (prevent click jitter)
            if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
                hasDraggedRef.current = true;
            }

            const newX = dragStartRef.current.fabX + dx;
            const newY = clampY(dragStartRef.current.fabY + dy);

            setPosition({
                x: Math.max(0, Math.min(newX, window.innerWidth - FAB_SIZE)),
                y: newY,
                side: newX + FAB_SIZE / 2 < window.innerWidth / 2 ? 'left' : 'right',
            });
        },
        [],
    );

    const handlePointerUp = useCallback(
        (e: React.PointerEvent) => {
            if (!dragStartRef.current) return;
            (e.target as HTMLElement).releasePointerCapture(e.pointerId);
            dragStartRef.current = null;
            setIsDragging(false);

            // Snap to nearest edge
            setIsSnapping(true);
            setPosition((prev) => {
                const snapX =
                    prev.side === 'left'
                        ? EDGE_MARGIN
                        : window.innerWidth - FAB_SIZE - EDGE_MARGIN;
                const snapped = { x: snapX, y: prev.y, side: prev.side };
                savePosition(snapped);
                return snapped;
            });

            setTimeout(() => setIsSnapping(false), SNAP_TRANSITION_MS);

            // If user didn't drag, treat as a click
            if (!hasDraggedRef.current) {
                onClick();
            }
        },
        [onClick],
    );

    const desktopStyle: React.CSSProperties = {
        position: 'fixed',
        left: position.x,
        top: position.y,
        zIndex: 1400,
        cursor: isDragging ? 'grabbing' : 'grab',
        transition: isSnapping
            ? `left ${SNAP_TRANSITION_MS}ms cubic-bezier(0.34, 1.56, 0.64, 1), top ${SNAP_TRANSITION_MS}ms ease`
            : 'none',
        userSelect: 'none',
        touchAction: 'none',
    };

    const fabSx = {
        // Ink in both modes (secondary.main flips to cream in dark, under a white icon).
        background: `linear-gradient(135deg, ${brand.ink} 0%, ${theme.palette.secondary.dark} 100%)`,
        color: '#fff',
        boxShadow: isDragging
            ? '0 8px 32px rgba(0,0,0,0.35)'
            : '0 4px 16px rgba(0,0,0,0.2)',
        transform: isDragging ? 'scale(1.1)' : isPanelOpen ? 'scale(0.9)' : 'scale(1)',
        transition: isDragging
            ? 'box-shadow 0.15s ease, transform 0.15s ease'
            : 'box-shadow 0.3s ease, transform 0.3s ease',
        '&:hover': {
            background: `linear-gradient(135deg, ${brand.inkSoft} 0%, ${brand.ink} 100%)`,
        },
    };

    const tooltipPlacement = position.side === 'left' ? 'right' : 'left';

    return (
        <div
            style={desktopStyle}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            data-testid="support-hub-fab-wrapper"
        >
            <Tooltip
                title={isPanelOpen ? '' : 'Need Help?'}
                placement={isMobile ? 'right' : tooltipPlacement}
                arrow
            >
                <Badge
                    badgeContent={badgeCount}
                    color="error"
                    invisible={badgeCount === 0}
                >
                    <Fab
                        aria-label="Open Support Hub"
                        sx={fabSx}
                        data-testid="support-hub-fab"
                    >
                        <SupportAgentIcon />
                    </Fab>
                </Badge>
            </Tooltip>
        </div>
    );
};

export default DraggableFab;
